import BaseCustomizer from './base';
import renderReactComponentFactory, { createWrapper, FactoryReturnType } from '../utils/react-utils';
import Header, { FontSize } from '../components/common/Header';
import ButtonBar from '../components/common/ButtonBar';
import ErrorContainer from '../components/common/ErrorContainer';
import InfoContainer from '../components/common/InfoContainer';
import { ReactNode } from 'react';
import EventBus from '../utils/event-bus';
import { isAthenaOneWebOrMobileClient } from '../utils/app-utils';
import { AthenaOneParams } from '../utils/banner-jwt-utils';
import ProxyFooter from '../components/common/ProxyFooter';
import InputFieldFactory from '../components/common/InputFieldFactory';
import { getSelectedFactorType, getSelectedProvider, getUsername } from '../utils/widget-facade';
import MutedText from '../components/common/MutedText';
import { getAdminSupportPhrase, prefetchAdminCoverage } from '../utils/admin-coverage-utils';

interface ControllerContext {
  athenaOneParams?: AthenaOneParams;
  athenaOneWebSso?: boolean;
  athenaOneWebSessionTimedOut?: boolean;
  athenaOneWebNonSsoTimedOut?: boolean;
}

export default abstract class BaseForgeCustomizer extends BaseCustomizer {
  protected controllerContext: ControllerContext = {};
  protected displayFooterText: boolean = false;
  protected hideOktaFooter: boolean = false;
  protected renderTerminalView: boolean = false;
  protected headerDescription: string = '';
  protected headerText?: string = '';
  protected headerFontSize: FontSize = 'default';
  protected displayHeader: boolean = true;
  protected primaryButtonFullWidth: boolean = true;
  protected primaryButtonOnClick?: (buttonText: string) => Promise<void> | void;
  protected suppressOktaClick: boolean = false;
  protected inputFieldsCustomClass?: string;
  protected eventEmitter = new EventBus();
  protected primaryButtonClassName: string = '';
  protected renderReactComponent: FactoryReturnType = () => undefined;
  protected provider = getSelectedProvider();
  protected factorType = getSelectedFactorType();
  protected secondaryButtonOnClick?: any;
  protected skipAddFooter?: boolean;
  protected tertiaryButtonOnClick?: (buttonText: string) => void;
  protected onInfoContainerLinkClick?: (buttonText: string) => void;
  protected alwaysDisplayBackToLogin = false;
  protected primaryButtonLabel: string | undefined = undefined;
  protected allowRegistration: boolean = false;
  protected headerBorder: boolean = !isAthenaOneWebOrMobileClient();

  hideList: Array<any> = [];
  showList: Array<any> = [];

  get footerText(): ReactNode | undefined {
    if (this.displayFooterText) {
      return (
        <>
          {'If you need help setting up or managing authentication methods, '}
          {getAdminSupportPhrase()}
        </>
      );
    }
    return undefined;
  }

  protected addAthenaOneLogo(signInHeader: JQuery<HTMLElement>) {
    const headerLogo = signInHeader.find('img').addClass('athena-one-logo');
    const identityStaticAssetsUrl = process.env.REACT_APP_IDENTITY_STATIC_ASSETS_URL;
    if (headerLogo.length && identityStaticAssetsUrl) {
      headerLogo[0].src = `${identityStaticAssetsUrl}/athenaOneLogo.png`;
    }
  }

  protected customizeLogo() {
    const signInHeader: JQuery<HTMLElement> = jQueryCourage('.okta-sign-in-header');
    if (signInHeader.length) {
      if (isAthenaOneWebOrMobileClient()) {
        this.addAthenaOneLogo(signInHeader);
      }
    }
  }

  async customize() {
    this.renderReactComponent = renderReactComponentFactory({
      eventEmitter: this.eventEmitter,
      controller: this.context?.controller,
      provider: this.provider,
      factorType: this.factorType,
      settings: this.settings,
      ...this.controllerContext,
    });
    jQueryCourage('.auth-content').css('opacity', '');

    // If footer text needed, skip footer initially, prefetch data, then add footer
    if (this.displayFooterText) {
      this.skipAddFooter = true;
      this.forgifyController();
      this.replaceComponents();
      this.skipAddFooter = false;

      await prefetchAdminCoverage(getUsername());
      this.addFooter();
    }
    else {
      this.forgifyController();
      this.replaceComponents();
    }
  }

  protected forgifyController() {
    this.customizeLogo();
    this.forgifyWidgetContainer();
    this.forgifyHeader();
    this.forgifyInfoContainer();
    this.forgifyErrorContainer();
    this.forgifyInputFields();
    this.forgifyButtonBar();
    if (!this.skipAddFooter) {
      this.addFooter();
    }
  }

  private forgifyInputFields() {
    const inputFieldContainers: JQuery = jQueryCourage('.o-form-fieldset');
    if (inputFieldContainers.length) {
      inputFieldContainers.each((index) => {
        const inputFieldContainer = inputFieldContainers.eq(index);
        const selectField = inputFieldContainer.find('select');
        const inputField = selectField.length ? selectField : inputFieldContainer.find('input');
        const component = (
          <InputFieldFactory inputFieldContainer={inputFieldContainer} className={this.inputFieldsCustomClass} />
        );
        this.renderForgeAndHideOktaComponent({
          id: `${inputField.attr('name')! || inputField.attr('id')!}-input-field-container`,
          forgeComponent: component,
          oktaComponent: inputFieldContainer,
        });
      });
    }
  }

  private forgifyHeader() {
    const oktaHeader: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-head"]');
    const oktaSubtitle = jQueryCourage('[data-se="o-form-explain"]');
    if (oktaHeader.length || oktaSubtitle.length) {
      if (this.displayHeader) {
        const component = (
          <Header
            oktaHeader={oktaHeader}
            oktaSubtitle={oktaSubtitle}
            description={this.headerDescription}
            headerText={this.headerText}
            fontSize={this.headerFontSize}
          />
        );
        this.renderForgeAndHideOktaComponent({
          oktaComponent: oktaHeader,
          forgeComponent: component,
          id: oktaHeader.attr('data-se')!,
        });
        oktaSubtitle[0] && this.addToHideList(oktaSubtitle[0]);
      }
      else {
        this.addToHideList(oktaHeader[0]);
      }
    }
  }

  protected getFooter({ footerLink }: { footerLink: JQuery }) {
    return (
      <ProxyFooter
        text={this.footerText}
        footerLinks={footerLink}
        alwaysDisplayBackToSignIn={this.alwaysDisplayBackToLogin}
      />
    );
  }

  protected addFooter() {
    const footerLink = jQueryCourage('div.auth-footer a');
    if (!this.hideOktaFooter && (this.displayFooterText || footerLink?.length || this.alwaysDisplayBackToLogin)) {
      const oktaAuthContainer = jQueryCourage('.auth-footer').length
        ? jQueryCourage('.auth-footer')
        : jQueryCourage('.auth-content-inner form');
      const footer = createWrapper({ hidden: false });
      oktaAuthContainer.after(footer);
      const component = this.getFooter({ footerLink });
      this.renderReactComponent({
        id: 'footer',
        container: footer,
        component,
      });
    }
  }

  private forgifyButtonBar() {
    const buttonBar: JQuery<HTMLElement> = jQueryCourage('.o-form-button-bar');
    if (buttonBar.length) {
      const component = (
        <ButtonBar
          oktaButtonBar={buttonBar}
          fullWidth={this.primaryButtonFullWidth}
          primaryButtonOnClick={this.primaryButtonOnClick}
          primaryButtonClassName={this.primaryButtonClassName}
          secondaryButtonOnClick={this.secondaryButtonOnClick}
          suppressOktaClick={this.suppressOktaClick}
          tertiaryButtonOnClick={this.tertiaryButtonOnClick}
          primaryButtonLabel={this.primaryButtonLabel}
          allowRegistration={this.allowRegistration}
        />
      );
      this.renderForgeAndHideOktaComponent({
        id: buttonBar.attr('class')!,
        oktaComponent: buttonBar,
        forgeComponent: component,
      });
    }
  }

  protected forgifyErrorContainer() {
    const errorContainers: Readonly<JQuery> = jQueryCourage('div[data-se="o-form-error-container"]');
    if (errorContainers.length) {
      errorContainers.each((index) => {
        const errorContainer = errorContainers.eq(index);
        const forgeComponent = (
          <ErrorContainer
            oktaErrorContainer={errorContainer}
            renderTerminalView={this.renderTerminalView}
            backToLoginOktaFooterButton={jQueryCourage('.auth-footer').find('a.js-cancel')}
          />
        );
        this.renderForgeAndHideOktaComponent({
          /*
              As Okta error containers are not having `id` attribute, we are relying `index` value to uniquely identify.
              By making `errorContainers` immutable we ensure that only `id` remains unique.
            */
          id: `${errorContainer.attr('class')}${index}`,
          oktaComponent: errorContainer,
          forgeComponent: forgeComponent,
        });
      });
    }
  }

  protected forgifyWidgetContainer() {
    const oktaSignInContainer = jQueryCourage('#okta-sign-in');
    if (oktaSignInContainer.length) {
      oktaSignInContainer.addClass('rounded-border');
      oktaSignInContainer.find('.auth-header').css({ border: this.headerBorder ? '' : 'none' });
    }
  }

  protected renderForgeAndHideOktaComponent({
    id,
    oktaComponent,
    forgeComponent,
    className,
  }: {
    id: string;
    oktaComponent: JQuery;
    forgeComponent: ReactNode;
    className?: string;
  }) {
    if (oktaComponent.length) {
      const container = createWrapper();
      oktaComponent.after(container);
      this.renderReactComponent({
        id,
        container,
        component: forgeComponent,
        className,
      });
      this.addToHideList(oktaComponent[0]);
      this.addToShowList(container);
    }
  }

  addToHideList(element?: HTMLElement) {
    if (element) {
      this.hideList.push(element);
    }
  }

  addToShowList(element: HTMLElement) {
    if (element) {
      this.showList.push(element);
    }
  }

  protected replaceComponents() {
    if (this.hideList.length) {
      this.hideList.forEach((el) => this.hideComponent(el));
      this.hideList.length = 0;
    }
    if (this.showList.length) {
      this.showList.forEach((el) => el.classList.remove('hide-element'));
      this.showList.length = 0;
    }
  }

  protected hideComponent(element: HTMLElement) {
    element?.classList.add('hide-element');
  }

  private forgifyInfoContainer() {
    const infoContainer: JQuery<HTMLElement> = jQueryCourage('.o-form-info-container');
    if (infoContainer.length) {
      const forgeComponent = (
        <InfoContainer oktaInfoContainer={infoContainer} onLinkClick={this.onInfoContainerLinkClick} />
      );
      this.renderForgeAndHideOktaComponent({
        id: infoContainer.attr('class')!,
        oktaComponent: infoContainer,
        forgeComponent: forgeComponent,
      });
    }
  }

  protected forgifySubTitle(selector: string) {
    const oktaComponent: JQuery = jQueryCourage(selector).eq(0);
    const component = (
      <MutedText className="fe_u_font-size--default fe_u_text-align--center" text={oktaComponent.text()} />
    );
    this.renderForgeAndHideOktaComponent({
      id: oktaComponent.attr('class')!,
      forgeComponent: component,
      oktaComponent,
    });
  }
}
